import React, { useEffect, useMemo, useRef, useState } from 'react';
import './BattleshipsGame.css';

const GRID_SIZE = 8;
const CELL_PX = 70;
const CELL_GAP = 6; // MUST match .battleships-board { gap: 6px; }
const spanPx = (cells) => cells * CELL_PX + (cells - 1) * CELL_GAP;

/* ---------- Boats ---------- */
const BASE_BOATS = [
  {
    id: 'small',
    name: 'Patrol Boat',
    size: 1,
    orientation: 'horizontal',
    image:
      'https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FBattleships%201%20square.png?alt=media&token=61c51bda-96e1-4336-8bac-eb22792e1009',
  },
  {
    id: 'submarine',
    name: 'Submarine',
    size: 1,
    orientation: 'horizontal',
    image:
      'https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/Battleships%2F1%20square%20ship.png?alt=media&token=c2cdb746-38a9-4147-9b61-f5aac4a4fe25',
  },
  {
    id: 'cruiser',
    name: 'Cruiser',
    size: 3,
    orientation: 'horizontal',
    image:
      'https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/Battleships%2F3%20square%20ship.png?alt=media&token=183ca75d-eb56-4618-8600-3e763fc30836',
  },
  {
    id: 'patrol',
    name: 'Destroyer',
    size: 2,
    orientation: 'horizontal',
    image:
      'https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FBattleships%202%20squares%20across.png?alt=media&token=82f42a1b-8a83-4264-b71d-974458c40f59',
  },
];

/* ---------- Board helpers ---------- */
const makeEmptyBoard = () =>
  Array(GRID_SIZE)
    .fill(null)
    .map(() => Array(GRID_SIZE).fill(null));

function inBounds(r, c) { return r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE; }

function cellsForPlacement(row, col, size, orientation) {
  return Array.from({ length: size }, (_, i) =>
    orientation === 'horizontal' ? [row, col + i] : [row + i, col]
  );
}

function canPlace(board, row, col, boat) {
  const coords = cellsForPlacement(row, col, boat.size, boat.orientation);
  return coords.every(([r, c]) => inBounds(r, c)) && coords.every(([r, c]) => !board[r][c]);
}

function placeBoat(board, row, col, boat) {
  const next = board.map((r) => r.slice());
  const coords = cellsForPlacement(row, col, boat.size, boat.orientation);
  coords.forEach(([r, c], i) => { next[r][c] = { shipId: boat.id, anchor: i === 0 }; });
  return {
    board: next,
    placedBoatInfo: {
      id: boat.id,
      row,
      col,
      orientation: boat.orientation,
      size: boat.size,
      image: boat.image,
      name: boat.name,
    },
  };
}

function randomInt(max) { return Math.floor(Math.random() * max); }

function randomPlaceAllBoats(boats) {
  let board = makeEmptyBoard();
  const placements = [];
  for (const baseBoat of boats) {
    const boat = { ...baseBoat }; // keep horizontal only
    let placed = false;
    let guard = 0;
    while (!placed && guard < 500) {
      guard++;
      const row = randomInt(GRID_SIZE);
      const col = randomInt(GRID_SIZE);
      if (canPlace(board, row, col, boat)) {
        const { board: b2, placedBoatInfo } = placeBoat(board, row, col, boat);
        board = b2;
        placements.push(placedBoatInfo);
        placed = true;
      }
    }
    if (!placed) return randomPlaceAllBoats(boats);
  }
  return { board, placements };
}

/* ---------- Quiz ---------- */
function makeQuestion(level = 1) {
  const a = Math.floor(Math.random() * (level >= 2 ? 12 : 20)) + 1;
  const b = Math.floor(Math.random() * (level >= 2 ? 12 : 20)) + 1;
  const ops = level === 1 ? ['+', '-'] : level === 2 ? ['×'] : ['+', '-', '×'];
  const op = ops[Math.floor(Math.random() * ops.length)];
  const prompt = `${a} ${op} ${b}`;
  const answer = op === '+' ? a + b : op === '-' ? a - b : a * b;
  return { a, b, op, prompt, answer };
}

export default function BattleshipsGame() {
  /* Phase / winner */
  const [phase, setPhase] = useState('placement');
  const [winner, setWinner] = useState(null);
// suspense/explosion before revealing hit or miss
const [explodingCell, setExplodingCell] = useState(null);   // {row,col} | null
const [resolvingShot, setResolvingShot] = useState(false);  // lock clicks while exploding

  /* Player & opponent */
  const [playerBoard, setPlayerBoard] = useState(makeEmptyBoard());
  const [playerPlaced, setPlayerPlaced] = useState([]);
  const [availableBoats, setAvailableBoats] = useState(BASE_BOATS);
  const [opponentBoard, setOpponentBoard] = useState(makeEmptyBoard());
  const [opponentPlaced, setOpponentPlaced] = useState([]);

  const allBoatsPlaced = availableBoats.length === 0;
// near GRID_SIZE / CELL_PX
const EXPLOSION_DELAY_MS = 420; // ↓ smaller = faster reveal

  /* Turns / quiz */
  const [turn, setTurn] = useState('player');
  const [pendingShot, setPendingShot] = useState(null);
  const [question, setQuestion] = useState(null);
  const [answerInput, setAnswerInput] = useState('');

  /* Pointer-drag state */
  const [draggingBoat, setDraggingBoat] = useState(null);
  const [hoverCell, setHoverCell] = useState(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  /* Refs for global handlers */
  const dragSrcElRef = useRef(null);
  const draggingBoatRef = useRef(null);
  const hoverCellRef = useRef(null);
  const playerBoardRef = useRef(playerBoard);
  const didMoveRef = useRef(false);

  useEffect(() => { draggingBoatRef.current = draggingBoat; }, [draggingBoat]);
  useEffect(() => { hoverCellRef.current = hoverCell; }, [hoverCell]);
  useEffect(() => { playerBoardRef.current = playerBoard; }, [playerBoard]);

  const pt = (ev) => (ev.touches ? ev.touches[0] : ev);

  /* ----- PICKUP ----- */
  const handlePickUp = (ev, boat) => {
    const e = pt(ev);
    if (!ev.touches && ev.button !== 0) return; // left button only
    ev.preventDefault();                         // block native image drag
    didMoveRef.current = false;

    setDraggingBoat(boat);
    draggingBoatRef.current = boat;
    dragSrcElRef.current = ev.currentTarget;

    const rect = ev.currentTarget.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setDragPos({ x: e.clientX, y: e.clientY });

    // visually 'pick up' the source image
    ev.currentTarget.style.visibility = 'hidden';

    // global listeners
    window.addEventListener('mousemove', handleDragMove, { passive: false });
    window.addEventListener('mouseup', handleDropOrCancel);
    window.addEventListener('touchmove', handleDragMove, { passive: false });
    window.addEventListener('touchend', handleDropOrCancel);
    window.addEventListener('blur', handleDropOrCancel);
  };

  /* ----- MOVE ----- */
  const handleDragMove = (ev) => {
    const e = pt(ev);
    setDragPos({ x: e.clientX, y: e.clientY });

    if (!didMoveRef.current) {
      didMoveRef.current =
        Math.abs(e.movementX || 0) + Math.abs(e.movementY || 0) > 2 || !!ev.touches;
    }

    const el = document.elementFromPoint(e.clientX, e.clientY);
    const cellEl = el && el.closest ? el.closest('.battleships-cell') : null;

    if (cellEl && phase === 'placement') {
      setHoverCell({ row: Number(cellEl.dataset.row), col: Number(cellEl.dataset.col) });
    } else {
      setHoverCell(null);
    }

    if (ev.cancelable) ev.preventDefault(); // stop page scroll on touch
  };

  /* ----- DROP/CANCEL ----- */
  const handleDropOrCancel = () => {
    const boat = draggingBoatRef.current;
    const hover = hoverCellRef.current;
    const boardNow = playerBoardRef.current;

    if (boat && hover && canPlace(boardNow, hover.row, hover.col, boat)) {
      const { board: next, placedBoatInfo } = placeBoat(boardNow, hover.row, hover.col, boat);
      setPlayerBoard(next);
      setPlayerPlaced((p) => [...p, placedBoatInfo]);
      setAvailableBoats((prev) => prev.filter((b) => b.id !== boat.id));
    }

    if (dragSrcElRef.current) {
      dragSrcElRef.current.style.visibility = 'visible'; // always restore
      dragSrcElRef.current = null;
    }

    setDraggingBoat(null);
    setHoverCell(null);

    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDropOrCancel);
    window.removeEventListener('touchmove', handleDragMove);
    window.removeEventListener('touchend', handleDropOrCancel);
    window.removeEventListener('blur', handleDropOrCancel);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDropOrCancel);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDropOrCancel);
      window.removeEventListener('blur', handleDropOrCancel);
    };
  }, []);

  /* ----- Game flow ----- */
  const startGame = () => {
    const { board, placements } = randomPlaceAllBoats(BASE_BOATS);
    setOpponentBoard(board);
    setOpponentPlaced(placements);
    setPhase('play');
    setTurn('player');
    setWinner(null);
    setQuestion(null);
    setPendingShot(null);
    setAnswerInput('');
  };

  const resetAll = () => {
    setPhase('placement');
    setWinner(null);
    setPlayerBoard(makeEmptyBoard());
    setPlayerPlaced([]);
    setAvailableBoats(BASE_BOATS);
    setOpponentBoard(makeEmptyBoard());
    setOpponentPlaced([]);
    setTurn('player');
    setPendingShot(null);
    setQuestion(null);
    setAnswerInput('');
  };

  function applyShot(board, row, col) {
    const cell = board[row][col];
    const next = board.map((r) => r.slice());
    if (cell?.hit || cell?.miss) return { board: next, result: 'repeat' };
    if (cell && cell.shipId) {
      next[row][col] = { ...cell, hit: true };
      return { board: next, result: 'hit', shipId: cell.shipId };
    }
    next[row][col] = { ...(cell || {}), miss: true };
    return { board: next, result: 'miss' };
  }

  function isSunk(board, shipId, placedList) {
    const placed = placedList.find((b) => b.id === shipId);
    if (!placed) return false;
    const coords = cellsForPlacement(placed.row, placed.col, placed.size, placed.orientation);
    return coords.every(([r, c]) => board[r][c]?.hit);
  }

  function allShipsSunk(board, placedList) {
    return placedList.every((b) => isSunk(board, b.id, placedList));
  }

  const requestPlayerShot = (row, col) => {
  if (phase !== 'play' || turn !== 'player' || resolvingShot) return; // ⬅️ added check
  const cell = opponentBoard[row][col];
  if (cell?.hit || cell?.miss) return;
if (cell && cell.shipId) {
  // 🚢 It’s a ship square → gate it behind a question
   setPendingShot({ row, col });
  setQuestion(makeQuestion(2));
 setAnswerInput('');
} else {
  // 🌊 It’s empty water → auto-miss (no question)
 const { board: next } = applyShot(opponentBoard, row, col);
setOpponentBoard(next);
 setTurn('opponent');
}
};


  const submitAnswer = (e) => {
  e.preventDefault();
  if (!question || !pendingShot) return;

  const correct = Number(answerInput) === question.answer;
  if (!correct) {
    setQuestion(null);
    setPendingShot(null);
    setTurn('opponent');
    return;
  }

  // ✅ play explosion first
  const { row, col } = pendingShot;
  setResolvingShot(true);
  setExplodingCell({ row, col });
  setQuestion(null); // hide the quiz while we explode

  // after the explosion animation finishes, actually apply the shot
  setTimeout(() => {
    const { board: next, result, shipId } = applyShot(opponentBoard, row, col);
    setOpponentBoard(next);
    setPendingShot(null);
    setExplodingCell(null);

    let won = false;
    if (result === 'hit') {
      if (isSunk(next, shipId, opponentPlaced)) {/* optional toast */}
      if (allShipsSunk(next, opponentPlaced)) won = true;
    }
    if (won) {
      setPhase('gameover');
      setWinner('player');
    } else {
      setTurn('opponent');
    }
    setResolvingShot(false);
  }, EXPLOSION_DELAY_MS);
 // explosion duration (ms) – keep in sync with CSS
};


  useEffect(() => {
    if (phase !== 'play' || turn !== 'opponent') return;
    const timer = setTimeout(() => {
      const choices = [];
      for (let r = 0; r < GRID_SIZE; r++)
        for (let c = 0; c < GRID_SIZE; c++)
          if (!playerBoard[r][c]?.hit && !playerBoard[r][c]?.miss) choices.push([r, c]);
      if (!choices.length) return;
      const [row, col] = choices[Math.floor(Math.random() * choices.length)];
      const { board: next, result } = applyShot(playerBoard, row, col);
      setPlayerBoard(next);
      const lost = result === 'hit' && allShipsSunk(next, playerPlaced);
      if (lost) { setPhase('gameover'); setWinner('opponent'); } else { setTurn('player'); }
    }, 2900);
    return () => clearTimeout(timer);
  }, [turn, phase, playerBoard, playerPlaced]);

  const letters = useMemo(
    () => Array.from({ length: GRID_SIZE }, (_, i) => String.fromCharCode(65 + i)),
    []
  );

  /* ---------- Board ---------- */
  const Board = ({ board, showShips, onCellClick, explodingCell }) => (
    <div className="battleships-board">
      <div className="battleships-corner" />
      {letters.map((ch) => (
        <div key={`top-${ch}`} className="battleships-label label-top">{ch}</div>
      ))}
      {board.map((row, rIdx) => (
        <React.Fragment key={rIdx}>
          <div className="battleships-label label-side">{rIdx + 1}</div>
          {row.map((cell, cIdx) => {
            const key = `${rIdx}-${cIdx}`;
            const isTargetable = !!onCellClick && phase === 'play' && turn === 'player';
            const showPreview =
              phase === 'placement' && draggingBoat && hoverCell &&
              hoverCell.row === rIdx && hoverCell.col === cIdx;

            // look up this placed ship once
            const placed =
              playerPlaced.find((b) => b.id === cell?.shipId) ||
              opponentPlaced.find((b) => b.id === cell?.shipId);

            return (
              <div
                key={key}
                data-row={rIdx}
                data-col={cIdx}
                className={`battleships-cell
                  ${showShips && cell?.anchor ? 'has-ship' : ''}
                  ${isTargetable ? 'is-targetable' : ''}
                  ${
                    draggingBoat && hoverCell && hoverCell.row === rIdx && hoverCell.col === cIdx
                      ? (canPlace(board, rIdx, cIdx, draggingBoat) ? 'drop-ok' : 'drop-bad')
                      : ''
                  }`}
                onClick={() => onCellClick && onCellClick(rIdx, cIdx)}
              >
                {/* Render anchored ship image */}
                {showShips && cell?.anchor && placed && (
                  <img
                    src={placed.image}
                    alt={placed.name || 'ship'}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      zIndex: 10,
                      width:  placed.orientation === 'horizontal' ? `${spanPx(placed.size)}px` : `${CELL_PX}px`,
                      height: placed.orientation === 'vertical'   ? `${spanPx(placed.size)}px` : `${CELL_PX}px`,
                      pointerEvents: 'none',
                    }}
                  />
                )}

                {/* Live drag preview */}
                {showPreview && (
                  <img
                    className="drag-preview"
                    src={draggingBoat.image}
                    alt=""
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width:  draggingBoat.orientation === 'horizontal' ? `${spanPx(draggingBoat.size)}px` : `${CELL_PX}px`,
                      height: draggingBoat.orientation === 'vertical'   ? `${spanPx(draggingBoat.size)}px` : `${CELL_PX}px`,
                      pointerEvents: 'none',
                      outline: canPlace(board, rIdx, cIdx, draggingBoat)
                        ? '2px solid rgba(57,255,20,.9)'
                        : '2px solid rgba(255,80,80,.9)',
                      outlineOffset: 0,
                      opacity: 0.75,
                      filter: 'drop-shadow(0 3px 8px rgba(0,0,0,.5))',
                    }}
                  />
                )}
{/* Explosion overlay (plays before hit/miss is added) */}
{explodingCell &&
 explodingCell.row === rIdx &&
 explodingCell.col === cIdx && <div className="cell-explosion" />}

                {/* Hits / Misses */}
                
                {board[rIdx][cIdx]?.hit && <div className="hit-marker" aria-label="hit" />}
                {board[rIdx][cIdx]?.miss && <div className="miss-marker" aria-label="miss" />}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="battleships-wrapper">
      <h1>Battleships</h1>

      {phase === 'placement' ? (
  <>
    <p className="subtitle">Drag your ships onto the board. When you’re done, press Start.</p>

    {/* ✅ one green frame that contains BOTH board and tray */}
    <div className="neon-frame">
      <div className="placing-2col">
        {/* LEFT: Board */}
        <div className="board-col">
          <Board board={playerBoard} showShips={true} onCellClick={null} />
        </div>

        {/* RIGHT: Tray (now inside the frame) */}
        <aside className="tray-col">
          <h2>Available Ships</h2>

          <div className="boats-rail">
            {availableBoats.map((boat) => (
              <div key={boat.id} className="boat-button boat-button--rail">
                <img
                  src={boat.image}
                  alt={boat.name}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onMouseDown={(e) => handlePickUp(e, boat)}
                  onTouchStart={(e) => handlePickUp(e, boat)}
                  style={{
                    width: `${spanPx(boat.size)}px`,
                    height: `${CELL_PX}px`,
                    cursor: draggingBoat?.id === boat.id ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    touchAction: 'none',
                    WebkitUserDrag: 'none',
                  }}
                />
                <span className="boat-caption">
                  {boat.name} • {boat.size} {boat.size > 1 ? ' squares' : ' square'}
                </span>
              </div>
            ))}
          </div>

          <div className="actions actions--rail">
            <button className="start-game-btn" disabled={!allBoatsPlaced} onClick={startGame}>
              Start Game
            </button>
            <button className="reset-btn" onClick={resetAll}>Reset</button>
          </div>
        </aside>
      </div>
    </div>
  </>
) : (
  /* your existing non-placement UI unchanged */
  <>
    <div className="status-bar">
      {phase === 'play' ? (
        <strong>{turn === 'player' ? 'Your turn' : "Opponent's turn…"}</strong>
      ) : (
        <strong>{winner === 'player' ? 'You win! 🎉' : 'You lose. Try again!'}</strong>
      )}
      <button className="reset-btn small" onClick={resetAll}>New Game</button>
    </div>

    <div className="boards-2col">
      <div>
        <h2>Your Fleet</h2>
        <Board board={playerBoard} showShips={true} onCellClick={null} />
      </div>
    <div>
  <h2>Enemy Waters</h2>
  <Board
    board={opponentBoard}
    showShips={false}
    onCellClick={requestPlayerShot}
    explodingCell={explodingCell}   
  />
  <p className="hint">
    Click a cell to fire — but you’ll need to answer a question first!
  </p>
</div>

    </div>

    {phase === 'play' && question && pendingShot && (
      <form className="quiz-panel" onSubmit={submitAnswer}>
        <div className="quiz-row">
          <span className="quiz-q">Solve: {question.prompt}</span>
          <input
            className="quiz-input"
            type="number"
            inputMode="numeric"
            value={answerInput}
            onChange={(e) => setAnswerInput(e.target.value)}
            autoFocus
            placeholder="Answer"
          />
          <button className="quiz-submit" type="submit">Fire!</button>
          <button type="button" className="quiz-cancel" onClick={() => { setQuestion(null); setPendingShot(null); }}>
            Cancel
          </button>
        </div>
        <small className="quiz-help">Tip: wrong answers skip your turn.</small>
      </form>
    )}
  </>
)}


      {/* Floating ship that follows the cursor/touch while dragging */}
      {draggingBoat && (
        <img
          src={draggingBoat.image}
          alt=""
          className="floating-ship"
          style={{
            position: 'fixed',
            left: dragPos.x - dragOffset.x,
            top:  dragPos.y - dragOffset.y,
            width:  draggingBoat.orientation === 'horizontal' ? `${spanPx(draggingBoat.size)}px` : `${CELL_PX}px`,
            height: draggingBoat.orientation === 'vertical'   ? `${spanPx(draggingBoat.size)}px` : `${CELL_PX}px`,
            pointerEvents: 'none',
            zIndex: 9999,
            opacity: 0.95,
            filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.35))',
          }}
        />
      )}
    </div>
  );
}
